import { Props } from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../constants';
import { StorePerformanceItem } from '../../../../hooks';
import { Charts } from '../../../../stories/molecules/charts/Charts';
import { Colors } from '../../../../theme';
import { homeTranslations } from '../../../../translations';
import { getFormattedTimeUnit } from '../../../../utils';

type WidgetChartProps<T extends StorePerformanceItem> = {
  performanceItemsChartName: string | string[];
  performanceItems: T[];
  filteredPerformanceItems: (T | null)[];
  forecastPerformanceItems: (T | null)[];
  itemValueResolver: (storePerformanceItem: T, seriesIndex?: number) => number;
  type?: Props['type'];
  seriesCount?: number;
  targetItems?: (number | null)[];
  showGridYAxis?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  dataLabel?: ApexDataLabels;
  yAxisTickAmount?: number;
  yAxisMax?: number;
  yAxisMin?: number;
  interval: StorePerformanceInterval;
  customYAxis?: ApexYAxis;
  chartColors?: Colors[];
  dashArray?: number[];
  isStacked?: boolean;
  sparklineEnabled?: boolean;
  showXAxisTooltip?: boolean;
  xAxisResolver?: (item: StorePerformanceItem) => string;
  yAxisFormatter?: (val: number) => string;
  tooltipFormatter?: (val: number) => string;
};

export default function WidgetChart<T extends StorePerformanceItem>(props: WidgetChartProps<T>) {
  const {
    performanceItemsChartName,
    performanceItems,
    filteredPerformanceItems,
    forecastPerformanceItems,
    itemValueResolver,
    type = 'area',
    seriesCount = 1,
    targetItems,
    showGridYAxis = true,
    showXAxis = false,
    showYAxis = true,
    dataLabel,
    yAxisTickAmount,
    yAxisMax,
    yAxisMin,
    interval,
    customYAxis,
    chartColors = ['validationGreen', 'tagBlue'],
    dashArray = [0, 4],
    isStacked = false,
    sparklineEnabled = false,
    showXAxisTooltip = false,
    xAxisResolver,
    yAxisFormatter,
    tooltipFormatter,
  } = props;

  const { t } = useTranslation();
  const { common: commonTranslations } = homeTranslations;

  const getSeriesType = (chartType: Props['type'], isForecast: boolean): Props['type'] => {
    switch (chartType) {
      case 'area':
        return !isForecast ? 'area' : 'line';
      case 'bar':
        return 'bar';
      default:
        return 'area';
    }
  };

  const getChartName = (chartName: string | string[], seriesIndex?: number) => {
    if (typeof chartName === 'string') return chartName;

    if (seriesIndex === undefined) return chartName[0];

    return chartName[seriesIndex];
  };

  const chartSeries: ApexAxisChartSeries = [];

  let chartForecastCount = 0;

  const combinedPerformanceItems = filteredPerformanceItems.map((item, index) => {
    if (forecastPerformanceItems[index]) chartForecastCount += 1;
    return forecastPerformanceItems[index] ?? item;
  });

  for (let i = 0; i < seriesCount; i++) {
    chartSeries.push({
      name: getChartName(performanceItemsChartName, i),
      type: getSeriesType(type, false),
      data: combinedPerformanceItems.map((item) => (item ? itemValueResolver(item, i) : null)),
    });
  }

  if (targetItems) {
    chartSeries.push({
      name: getChartName(performanceItemsChartName),
      type: 'line',
      data: targetItems,
    });
  }

  const xAxisCategories = showXAxis
    ? performanceItems.map((item) => {
        if (xAxisResolver) {
          return xAxisResolver(item);
        }
        return getFormattedTimeUnit(item.timeUnit, interval); // Converting Date to day of the week
      })
    : [];

  return (
    <Charts
      chartType={type}
      isStacked={isStacked}
      areaChartGradient={type === 'bar' ? { opacity: 1 } : undefined}
      chartPlotOptions={{
        columnWidth: '12px',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      }}
      isAnimationEnabled={false}
      xAxisCategories={xAxisCategories}
      chartSeries={chartSeries}
      chartColors={chartColors}
      legend={{ show: false }}
      xAxis={{
        labels: { show: showXAxis },
        tooltip: {
          enabled: false, // this disables the white bottom tooltip for x-axis
        },
      }}
      chartForecast={{ count: chartForecastCount, fillOpacity: 0.05 }}
      showGridYAxis={showGridYAxis}
      yAxis={{
        max: yAxisMax,
        min: yAxisMin,
        tickAmount: yAxisTickAmount,
        decimalsInFloat: 0,
        labels: {
          show: showYAxis,
          formatter: yAxisFormatter,
        },
        ...customYAxis,
      }}
      dashArray={dashArray}
      toolTip={{
        shared: true,
        intersect: false,
        y: {
          title: {
            formatter() {
              return '';
            },
          },
          formatter: function (value, { seriesIndex, dataPointIndex }) {
            const isForecastValue =
              chartForecastCount > 0 && dataPointIndex > combinedPerformanceItems.length - 1 - chartForecastCount;

            let prefix = '';

            if (seriesIndex > seriesCount - 1) {
              prefix = `${t(commonTranslations.hourlyTarget.key, commonTranslations.hourlyTarget.defaultValue)}: `;
            } else if (isForecastValue) {
              prefix = `${t(
                commonTranslations.forecast.key,
                commonTranslations.forecast.defaultValue,
              )} ${getChartName(performanceItemsChartName, seriesIndex)}: `;
            } else {
              prefix = `${getChartName(performanceItemsChartName, seriesIndex)}: `;
            }

            if (value === null) return `${prefix}-`;

            if (!tooltipFormatter) return `${prefix}${value.toString()}`;

            return `${prefix}${tooltipFormatter(value)}`;
          },
        },
        x: {
          show: showXAxisTooltip,
          formatter: (value, { dataPointIndex }) => {
            return `${t(commonTranslations.timeUnit.key, commonTranslations.timeUnit.defaultValue)}: ${
              xAxisCategories[dataPointIndex]
            }`;
          },
        },
      }}
      dataLabel={dataLabel}
      sparklineEnabled={sparklineEnabled}
    />
  );
}
